import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ymd, today0, type Settings as SettingsT, type Toggles } from '@biuro/shared';
import { api, type SyncResult } from '../api.ts';
import { useCopy, useData, useMe, useNav, useSaveSettings, useSettings, useToast } from '../hooks.tsx';
import { Icon, timeAgo } from '../ui.tsx';
import { RodoView } from './Rodo.tsx';

type TextKey = 'companyName' | 'nip' | 'regon' | 'address' | 'taxOffice' | 'microAccount' | 'accountantName' | 'accountantEmail' | 'accountantPhone';

function Field({ k, label, full, copy, hint, type, mode }: {
  k: TextKey; label: string; full?: boolean; copy?: boolean; hint?: ReactNode; type?: string; mode?: 'numeric' | 'email' | 'tel';
}) {
  const st = useSettings();
  const saveSettings = useSaveSettings();
  const doCopy = useCopy();
  const ref = useRef<HTMLInputElement>(null);
  const value = String(st[k] ?? '');
  return (
    <div className={`fld ${full ? 'full' : ''}`}>
      <label htmlFor={`s-${k}`}>{label}</label>
      <div className={copy ? 'copyrow' : ''}>
        <input id={`s-${k}`} key={value} ref={ref} defaultValue={value} type={type} inputMode={mode} autoComplete="off"
          onBlur={(e) => {
            let v = e.target.value.trim();
            if (k === 'nip') v = v.replace(/\D/g, '');
            if (v !== value) saveSettings({ [k]: v } as Partial<SettingsT>);
          }} />
        {copy && <button type="button" className="btn sm" onClick={() => doCopy(ref.current?.value || '')}>Kopiuj</button>}
      </div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

const KSEF = [
  ['recv', 'Biuro pobiera faktury zakupowe z KSeF', 'Odbieranie przez KSeF obowiązuje wszystkich od 1 lutego 2026.'],
  ['perm', 'Biuro ma nadane uprawnienia w KSeF', 'Nadajesz je w Aplikacji Podatnika KSeF, np. do odczytu faktur.'],
  ['infakt', 'Wystawianie faktur B2B w KSeF włączone w inFakt', ''],
  ['bl', 'Faktury z BaseLinkera trafiają do KSeF', 'Integracja BaseLinkera z KSeF albo wystawianie faktur B2B tylko w inFakt.'],
  ['limit', 'Do końca 2026 pilnuję limitu 10 000 zł brutto miesięcznie na faktury B2B poza KSeF', 'Po przekroczeniu limitu fakturę, która go przekroczyła, i wszystkie kolejne wystawiasz już w KSeF.'],
] as const;

export function Firma() {
  const { sub, go } = useNav();
  const [tab, setTab] = useState<'dane' | 'rodo'>(sub === 'rodo' ? 'rodo' : 'dane');
  useEffect(() => { setTab(sub === 'rodo' ? 'rodo' : 'dane'); }, [sub]);
  return (
    <>
      <div className="vh">
        <div>
          <h1>Firma</h1>
          <p>{tab === 'rodo'
            ? 'Rejestr czynności przetwarzania danych osobowych (art. 30 RODO) z podpowiedziami dla sklepu i usług B2B.'
            : 'Dane do szybkiego kopiowania, połączenia z Google i inFaktem oraz obowiązki widoczne w terminach. Zmiany zapisują się same.'}</p>
        </div>
      </div>
      <div className="toolbar">
        <div className="seg" role="group" aria-label="Widok">
          <button aria-pressed={tab === 'dane'} onClick={() => go('firma')}>Ustawienia</button>
          <button aria-pressed={tab === 'rodo'} onClick={() => go('firma', { sub: 'rodo' })}>Rejestr RODO</button>
        </div>
      </div>
      {tab === 'rodo' ? <RodoView /> : <Settings />}
    </>
  );
}

function Settings() {
  const { data } = useData();
  const me = useMe().data;
  const st = useSettings();
  const saveSettings = useSaveSettings();
  const toast = useToast();
  const doCopy = useCopy();
  const qc = useQueryClient();
  const [calRes, setCalRes] = useState<SyncResult | null>(null);
  const [gmRes, setGmRes] = useState<SyncResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [restore, setRestore] = useState<{ data: Record<string, Record<string, unknown>>; at?: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const vatEu = st.nip ? 'PL' + st.nip.replace(/\D/g, '') : '';
  const polaczono = new URLSearchParams(location.search).get('polaczono') === '1';

  const run = async (key: 'cal' | 'gm') => {
    setBusy(key);
    try {
      const r = key === 'cal' ? await api.syncCalendar() : await api.syncGmail();
      (key === 'cal' ? setCalRes : setGmRes)(r);
      toast(r.message);
      await qc.invalidateQueries();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const sw = (k: keyof Toggles, title: string, desc: string) => (
    <label className="sw">
      <input type="checkbox" checked={!!st.toggles[k]} onChange={(e) => saveSettings({ toggles: { ...st.toggles, [k]: e.target.checked } })} />
      <div><b>{title}</b><span>{desc}</span></div>
    </label>
  );
  const g = me?.google;
  const cal = calRes || me?.last.calendar;
  const gm = gmRes || me?.last.gmail;

  return (
    <>
      <h2 className="sec">Google Workspace</h2>
      <div className="panel pad stack">
        {polaczono && <div className="note ok">Połączono z Google. Terminy trafią do Kalendarza w ciągu kilku sekund.</div>}
        <div className="gsum">
          <div><b>{me?.owner || me?.email}</b><div className="muted small">Konto, którego Dysk, Kalendarz i Gmail obsługuje aplikacja</div></div>
          <div className="chips">
            <span className={`pill ${g?.drive ? 'ok' : ''}`}>Dysk {g?.drive ? 'połączony' : 'niepołączony'}</span>
            <span className={`pill ${g?.calendar ? 'ok' : ''}`}>Kalendarz {g?.calendar ? 'połączony' : 'niepołączony'}</span>
            <span className={`pill ${g?.gmail ? 'ok' : ''}`}>Gmail {g?.gmail ? 'połączony' : 'niepołączony'}</span>
          </div>
        </div>
        {me?.isOwner && !(g?.drive && g?.calendar && g?.gmail) && (
          <div><a className="btn hi" href="/auth/connect">Połącz Dysk, Kalendarz i Gmail</a><div className="hint">Aplikacja widzi na Dysku tylko pliki, które sama utworzy, w Kalendarzu tylko swój kalendarz „Terminy firmy”, a Gmail tylko odczytuje.</div></div>
        )}
        {g?.drive && me?.driveLink && <div><a className="btn" href={me.driveLink} target="_blank" rel="noreferrer"><Icon.ext />Folder faktur na Dysku</a></div>}
        {g?.calendar && (
          <div className="sync">
            <label className="sw" style={{ borderTop: 0, padding: 0 }}>
              <input type="checkbox" checked={st.calendarSync} onChange={(e) => saveSettings({ calendarSync: e.target.checked })} />
              <div><b>Terminy w Kalendarzu Google</b><span>Kalendarz „Terminy firmy” z przypomnieniem {st.leadDays} dni wcześniej i dzień przed terminem. Aktualizuje się po każdej zmianie i raz dziennie.</span></div>
            </label>
            <div className="sync-row"><span className="muted small">Ostatnio {timeAgo(cal?.at)}{cal?.message ? `: ${cal.message}` : ''}</span><button className="btn sm" disabled={busy === 'cal'} onClick={() => run('cal')}>{busy === 'cal' ? 'Synchronizuję…' : 'Synchronizuj teraz'}</button></div>
          </div>
        )}
        {g?.gmail && (
          <div className="sync">
            <label className="sw" style={{ borderTop: 0, padding: 0 }}>
              <input type="checkbox" checked={st.gmailSync} onChange={(e) => saveSettings({ gmailSync: e.target.checked })} />
              <div><b>Faktury z Gmaila</b><span>Co 6 godzin szukam maili z PDF-em od nadawców wpisanych w usługach. Plik trafia na Dysk, a faktura do uzupełnienia.</span></div>
            </label>
            <div className="sync-row"><span className="muted small">Ostatnio {timeAgo(gm?.at)}{gm?.message ? `: ${gm.message}` : ''}</span><button className="btn sm" disabled={busy === 'gm'} onClick={() => run('gm')}>{busy === 'gm' ? 'Sprawdzam…' : 'Sprawdź teraz'}</button></div>
            {!Object.values(data.services).some((s) => s.senders) && <div className="hint">Żadna usługa nie ma jeszcze wpisanych nadawców maili.</div>}
          </div>
        )}
        <div className="sync-row">
          <span className="muted small">Zalogowany: {me?.email}</span>
          <div className="actions">
            {me?.isOwner && (g?.drive || g?.calendar) && <button className="btn sm ghost" onClick={async () => { await api.disconnect(); await qc.invalidateQueries(); toast('Odłączono Google'); }}>Odłącz Google</button>}
            <button className="btn sm" onClick={async () => { await api.logout().catch(() => {}); location.href = '/'; }}>Wyloguj</button>
          </div>
        </div>
      </div>

      <h2 className="sec">inFakt</h2>
      <div className="panel pad">
        {me?.infakt.enabled
          ? <div className="note ok">Połączono przez klucz API{me.infakt.sandbox ? ' (środowisko testowe)' : ''}. Szablony dokumentów miesięcznych są w zakładce Zamknięcie miesiąca.</div>
          : <div className="note warn">Wygeneruj klucz w inFakcie (Ustawienia konta → API) i wpisz go na serwerze w pliku .env jako INFAKT_API_KEY, potem uruchom aplikację ponownie.</div>}
      </div>

      <h2 className="sec">Dane firmy</h2>
      <div className="panel pad">
        <div className="fgrid">
          <Field k="companyName" label="Nazwa firmy" copy />
          <Field k="nip" label="NIP" copy mode="numeric" />
          <div className="fld">
            <label htmlFor="s-vatue">Numer VAT-UE</label>
            <div className="copyrow"><input id="s-vatue" value={vatEu} readOnly placeholder="uzupełnij NIP" /><button className="btn sm" onClick={() => doCopy(vatEu)}>Kopiuj</button></div>
            <div className="hint">Podaj go zagranicznym dostawcom, żeby nie doliczali swojego VAT.</div>
          </div>
          <Field k="regon" label="REGON" copy mode="numeric" />
          <Field k="taxOffice" label="Urząd skarbowy" />
          <Field k="address" label="Adres" copy full />
          <Field k="microAccount" label="Mikrorachunek podatkowy" copy full mode="numeric"
            hint={<>Na niego płacisz VAT i ryczałt. Wygenerujesz go na <a href="https://www.podatki.gov.pl/generator-mikrorachunku-podatkowego" target="_blank" rel="noreferrer">podatki.gov.pl</a>.</>} />
        </div>
      </div>

      <h2 className="sec">Biuro rachunkowe</h2>
      <div className="panel pad">
        <div className="fgrid">
          <Field k="accountantName" label="Biuro / księgowa" />
          <Field k="accountantEmail" label="E-mail" copy type="email" mode="email" />
          <Field k="accountantPhone" label="Telefon" copy type="tel" mode="tel" />
          <div className="fld">
            <label htmlFor="s-docs">Dokumenty do biura do dnia</label>
            <input id="s-docs" key={st.docsDay} type="number" min={1} max={28} defaultValue={st.docsDay}
              onBlur={(e) => { const v = Math.min(28, Math.max(1, parseInt(e.target.value, 10) || 5)); if (v !== st.docsDay) saveSettings({ docsDay: v }); }} />
            <div className="hint">Dzień następnego miesiąca. Ustal go z biurem.</div>
          </div>
        </div>
      </div>

      <h2 className="sec">Obowiązki w terminach</h2>
      <div className="panel">
        {sw('vat', 'Przelew VAT', 'Do 25. dnia następnego miesiąca na mikrorachunek. Wyłącz, jeśli VAT płaci za Ciebie biuro.')}
        {sw('docs', 'Dokumenty do biura', 'Co miesiąc, w dniu ustawionym wyżej.')}
        {sw('infakt', 'Dokumenty w inFakcie', 'Faktury do kontrahentów i dokumenty wewnętrzne na koniec miesiąca.')}
        {sw('bdo', 'BDO: opłata roczna i sprawozdanie o opakowaniach', 'Do końca lutego i do 15 marca.')}
        {sw('rodo', 'Przegląd rejestru czynności przetwarzania', 'Raz w roku od ostatniego przeglądu rejestru RODO.')}
        {sw('ksef', 'Przejście na KSeF', 'Przypomnienie przed 1 stycznia 2027.')}
        {sw('vatue', 'Informacja VAT-UE', 'Tylko przy WDT, WNT albo usługach sprzedanych firmom z UE.')}
        {sw('oss', 'Deklaracja OSS (VIU-DO)', 'Kwartalnie, jeśli rozliczasz sprzedaż wysyłkową do konsumentów w UE przez OSS.')}
        <div className="sw" style={{ cursor: 'default' }}>
          <div style={{ flex: 1 }}><b>Śledź brakujące faktury od</b><span>Od tego miesiąca sprawdzam, czy każda usługa miesięczna ma fakturę.</span></div>
          <input type="month" className="inline" defaultValue={st.trackFrom} key={st.trackFrom} onBlur={(e) => e.target.value && e.target.value !== st.trackFrom && saveSettings({ trackFrom: e.target.value })} aria-label="Miesiąc początkowy" />
        </div>
      </div>

      <h2 className="sec">KSeF</h2>
      <div className="panel">
        {KSEF.map(([k, t, d]) => (
          <label className="sw" key={k}>
            <input type="checkbox" checked={!!st.ksefChecks[k]} onChange={(e) => saveSettings({ ksefChecks: { ...st.ksefChecks, [k]: e.target.checked } })} />
            <div><b>{t}</b>{d && <span>{d}</span>}</div>
          </label>
        ))}
        <div className="sw" style={{ cursor: 'default' }}><div><b>Faktury zagraniczne nie przechodzą przez KSeF</b><span>Biuro księguje je z faktury od dostawcy, którą dostaje w paczce.</span></div></div>
      </div>

      <h2 className="sec">Kopia zapasowa</h2>
      <div className="panel pad stack">
        <div className="muted small">Wszystkie usługi, faktury, terminy, szablony i ustawienia w jednym pliku JSON. Przyjmuje też kopię z wersji aplikacji w claude.ai.</div>
        <div className="actions">
          <a className="btn" href="/api/backup" download={`biuro-kopia-${ymd(today0())}.json`}>Pobierz kopię</a>
          <button className="btn" onClick={() => fileRef.current?.click()}>Przywróć z pliku</button>
          <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            try {
              const j = JSON.parse(await f.text());
              if (!j || typeof j !== 'object' || !(j.data || j.services)) throw new Error();
              setRestore({ data: j.data || j, at: j.exportedAt });
            } catch {
              toast('To nie jest plik kopii.');
            }
          }} />
        </div>
        {restore && (
          <div className="note hi">
            Kopia z {restore.at ? new Date(restore.at).toLocaleDateString('pl-PL') : 'nieznanej daty'}: {Object.keys(restore.data.services || {}).length} usług, {Object.keys(restore.data.invoices || {}).length} faktur. Wpisy o tych samych identyfikatorach zostaną nadpisane.
            <div className="actions" style={{ marginTop: 8 }}>
              <button className="btn pri sm" onClick={async () => {
                try {
                  const r = await api.restore({ data: restore.data });
                  toast(`Przywrócono ${r.saved} wpisów${r.rejected ? `, pominięto ${r.rejected}` : ''}.`);
                  setRestore(null);
                  await qc.invalidateQueries();
                } catch (err) {
                  toast((err as Error).message);
                }
              }}>Przywróć</button>
              <button className="btn sm" onClick={() => setRestore(null)}>Anuluj</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
